import React, {
  CSSProperties,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Drawer, {
  DEFAULT_DRAWER_WIDTH,
  DrawerProps,
  NEW_LOOK_DRAWER_CLOSE_HIT,
  NEW_LOOK_DRAWER_HEADER_TOP,
} from "common/Drawers/Drawer";
import styled, { css, useTheme } from "styled-components";
import Tabs from "common/Tabs/Tabs";
import Text from "common/Typography/Text";
import { getRouteWithoutPath } from "utils/routes";
import useMedia from "utils/hooks/useMedia";
import {
  DRAWER_VISIBILITY_CLASS,
  EXTRA_DETAILS_DRAWER_SCROLL_ID,
  INTERCOM_TOTAL_SIZE,
  IS_WEB_VIEW,
  MOBILE_SEARCH,
} from "common/constants";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { useAppDispatch } from "app/hooks";
import type { AppDispatch } from "app/store";
import { typography } from "@shared/common/Themes/typography";
import { useWindowSize } from "react-use";
import DemoModeBanner from "common/Home/Banners/DemoModeBanner";
import useMeasure from "utils/hooks/useMeasure";
import ScrollView from "common/Scroll/ScrollView";
import { Inset, QueryStatus } from "@shared/types/common/common.types";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import PageTabs from "common/Tabs/PageTabs.tsx";
import { PageWrapperContext } from "common/Home/PageWrapper/PageWrapper.context.tsx";
import useBackButtonHandler from "utils/hooks/useBackButtonHandler.ts";
import { ExtraDetailsDrawerContext } from "common/ExtraDetails/ExtraDetailsDrawer.context.ts";
import { isIOS, isMobile } from "react-device-detect";
import MobileSearch from "common/Home/Header/MobileSearch.tsx";
import { useTelegramSafeAreaInsets } from "utils/hooks/useTelegramWebApp.ts";
import { IS_TELEGRAM_WEB_APP } from "features/telegram/constants";
import {
  DRAWER_COLLAPSED_BAR_ID,
  DRAWER_SCROLL_ID,
  getDrawerTabId,
} from "testIds.ts";
import { NEW_LOOK_PHONE_GUTTER, phoneMinWidth } from "common/NewLook/layout.ts";

interface UseDrawerProps {
  path?: string;
  visible: boolean;
  tabsToComponents?: { [key: string]: React.JSX.Element | null };
  defaultTab?: string;
  title?: React.JSX.Element | string;
  tabContentLoaders?: Parameters<AppDispatch>[0][];
  hideCloseButton?: boolean;
  tab?: string;
  onTabChange?: (tab: string) => void;
  hideTabs?: boolean;
  onCloseDrawer?: (closeDrawerCallback?: () => void) => void;
  closeDrawer?: () => void;
  isLoading?: boolean;
  content?: React.JSX.Element | null;
  extension?: ReactNode;
  innerContentWrapperStyle?: CSSProperties;
  getTabLabel?: (tab: string) => ReactNode;
  minWidth?: number;
  hideExtensionBorder?: boolean;
  extensionWidth?: number;
  mainContentWidth?: number;
  tabToQueries?: Record<string, QueryStatus[]>;
  tabsStyle?: CSSProperties;
  flatListTabs?: string[];
  getTabLoadingData?: (tabKey: string) => {
    loading?: boolean;
    refreshing?: boolean;
  };
  extraComponent?: React.JSX.Element;
  showMobileSearch?: boolean;
  contentContainerStyle?: CSSProperties;
  collapsedTitle?: ReactNode;
}

const COLLAPSED_BAR_HEIGHT =
  NEW_LOOK_DRAWER_HEADER_TOP + NEW_LOOK_DRAWER_CLOSE_HIT;

const ExtraDetailsDrawer: FC<UseDrawerProps> = ({
  path,
  visible,
  tabsToComponents,
  title,
  tabContentLoaders,
  defaultTab,
  hideCloseButton,
  tab,
  onTabChange,
  hideTabs,
  onCloseDrawer,
  closeDrawer,
  isLoading,
  content,
  extension,
  innerContentWrapperStyle,
  flatListTabs,
  getTabLabel,
  minWidth,
  hideExtensionBorder,
  extensionWidth: propsExtensionWidth,
  mainContentWidth: propsMainContentWidth,
  tabToQueries,
  tabsStyle,
  getTabLoadingData,
  extraComponent,
  showMobileSearch,
  contentContainerStyle,
  collapsedTitle,
}) => {
  const { isNewLook } = useTheme();
  const { useMobileSearch, searchOpen, setSearchOpen, setShowSearch } =
    useContext(PageWrapperContext)!;
  const dispatch = useAppDispatch();
  const { historyReplace } = useHistoryWithQuery();
  const [localTab, setLocalTab] = useState(defaultTab);
  const tabs = Object.keys(tabsToComponents || {});
  const tabToUse = tab ?? localTab!;
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const drawerContentRef = useRef<HTMLDivElement>(null);
  const collapsibleScrollRef = useRef<HTMLDivElement>(null);
  const [collapsedBarVisible, setCollapsedBarVisible] = useState(false);
  const afterDrawerCloseCallbackRef = useRef<(() => void) | undefined>(
    undefined
  );
  const [measureBannerRef, { height: bannerHeight }] =
    useMeasure<HTMLDivElement>(undefined);
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const { registerBackButtonListener } = useBackButtonHandler();
  const { safeAreaInset, contentSafeAreaInset } = useTelegramSafeAreaInsets();
  const [tabKeyToQueries, setTabKeyToQueries] = useState<
    Record<string, QueryStatus[] | undefined>
  >({});

  const { width } = useWindowSize();
  const drawerWithExtensionContentWidth = useMedia(
    [`(max-width: 1200px)`, `(max-width: 1600px)`],
    [525, 550],
    600
  );
  const showDemoBanner = useMedia([`(max-width: 450px)`], [true], false);
  const mainContentWidth =
    propsMainContentWidth ?? drawerWithExtensionContentWidth;
  const totalDrawerWithExtensionWidth = propsExtensionWidth
    ? propsExtensionWidth + mainContentWidth
    : Math.min(width * 0.95, 1500);
  const extensionWidth =
    propsExtensionWidth ?? totalDrawerWithExtensionWidth - mainContentWidth;

  useEffect(() => {
    if (visible) {
      let backButtonCleanup: (() => void) | undefined;
      const handleWindowBackPress = !IS_WEB_VIEW;
      if (handleWindowBackPress) {
        window.onpopstate = () => {
          handleCloseDrawer();
        };
      }
      if (IS_WEB_VIEW || isIOS) {
        backButtonCleanup = registerBackButtonListener(() => {
          handleCloseDrawer();
          return true;
        });
      }
      return () => {
        if (handleWindowBackPress) window.onpopstate = null;
        backButtonCleanup?.();
      };
    }
  }, [visible]);

  useEffect(() => {
    if (visible) {
      setLocalTab(tab || defaultTab);
    }
  }, [visible, defaultTab]);

  useEffect(() => {
    if (!visible) setCollapsedBarVisible(false);
  }, [visible]);

  useEffect(() => {
    if (visible) {
      tabContentLoaders?.forEach((tabContentLoader) =>
        dispatch(tabContentLoader)
      );
    }
  }, [tabContentLoaders, visible]);

  useEffect(() => {
    if (showMobileSearch) {
      setShowSearch(true);
    }
  }, [showMobileSearch]);

  const handleCloseDrawer = () => {
    if (closeDrawer) {
      closeDrawer();
    } else if (path) {
      historyReplace(getRouteWithoutPath(location.pathname, path));
    }
  };

  const handleDrawerOpenChange = (open: boolean) => {
    if (!open && afterDrawerCloseCallbackRef.current) {
      const callback = afterDrawerCloseCallbackRef.current;
      afterDrawerCloseCallbackRef.current = undefined;
      callback();
    }
  };

  const closeDrawerBeforeWorkspaceSwitch = (switchWorkspace: () => void) => {
    if (!visible) {
      switchWorkspace();
      return;
    }

    const closeDrawerAndSwitchAfterClose = () => {
      afterDrawerCloseCallbackRef.current = switchWorkspace;
      handleCloseDrawer();
    };

    if (onCloseDrawer) {
      onCloseDrawer(closeDrawerAndSwitchAfterClose);
    } else {
      closeDrawerAndSwitchAfterClose();
    }
  };

  const localOnTabChange = (tab: string) => {
    if (visible) {
      onTabChange?.(tab);
    }
    setLocalTab(tab);
  };

  const getTabQueries = (tab: string) => {
    return tabToQueries?.[tab] ?? tabKeyToQueries?.[tab];
  };

  const onRefresh = (tab: string) => {
    try {
      getTabQueries(tab)?.forEach((query) => query.refetch());
    } catch {
      return;
    }
  };

  const isRefreshing = (tab: string) => {
    if (getTabLoadingData) {
      return getTabLoadingData(tab).refreshing;
    }
    return getTabQueries(tab)?.some(
      (query) => query.isFetching && !query.isLoading && !!query.currentData
    );
  };

  const getContent = () => {
    if (!isBottomNavigator || !!content) {
      return (
        <InnerContentWrapper
          style={innerContentWrapperStyle}
          id={EXTRA_DETAILS_DRAWER_SCROLL_ID}
        >
          <LoadingSpinnerOverlay
            isLoading={isLoading}
            spinnerMarginTop={50}
            spinnerSize="large"
          >
            <ContentContainer style={contentContainerStyle}>
              {content || tabsToComponents?.[tabToUse]}
            </ContentContainer>
            <div style={{ height: INTERCOM_TOTAL_SIZE }} />
          </LoadingSpinnerOverlay>
        </InnerContentWrapper>
      );
    }

    return (
      <LoadingSpinnerOverlay
        isLoading={isLoading}
        spinnerSize="large"
        hideWithOpacity={true}
      >
        {!!tabsItems?.length && (
          <PageTabs
            defaultKey={tabToUse}
            items={tabsItems}
            onTabChange={(tabItem) => {
              localOnTabChange(tabItem?.key);
            }}
            unmounting={!visible}
            hideTabs={hideTabs}
            scrollRef={isCollapsible ? collapsibleScrollRef : undefined}
            stickyTop={COLLAPSED_BAR_HEIGHT}
          />
        )}
      </LoadingSpinnerOverlay>
    );
  };

  const tabToId = (tab: string) => {
    return tab.toLowerCase().split(" ").join("-");
  };

  const setTabQueries = (
    tabKey: string,
    queries: QueryStatus[] | undefined
  ) => {
    setTabKeyToQueries((prev) => ({ ...prev, [tabKey]: queries }));
  };

  let drawerBodyStyle: CSSProperties = { padding: 0, overflow: "hidden" };
  if (extension)
    drawerBodyStyle = {
      ...drawerBodyStyle,
      display: "flex",
      width: totalDrawerWithExtensionWidth,
    };

  let props: DrawerProps = {
    onClose: () =>
      onCloseDrawer ? onCloseDrawer(handleCloseDrawer) : handleCloseDrawer(),
    open: visible,
    closable: !hideCloseButton,
    styles: { body: drawerBodyStyle },
    classNames: {
      section: DRAWER_VISIBILITY_CLASS,
      mask: DRAWER_VISIBILITY_CLASS,
    },
    afterOpenChange: handleDrawerOpenChange,
    closeOnBack: false,
  };
  props = {
    ...props,
    size: extension
      ? totalDrawerWithExtensionWidth
      : Math.max(DEFAULT_DRAWER_WIDTH, minWidth || 0),
  };

  let tabsItems = tabs?.map((tab) => ({
    key: tab,
    label: (
      <div id={`drawer-tab-${tabToId(tab)}`} data-testid={getDrawerTabId(tab)}>
        {getTabLabel ? getTabLabel(tab) : tab}
      </div>
    ),
  }));
  const isCollapsible =
    !!collapsedTitle && isNewLook && isBottomNavigator && !content;
  const tabBodyStyle = {
    paddingRight: isNewLook ? NEW_LOOK_PHONE_GUTTER : 12,
    paddingLeft: isNewLook ? NEW_LOOK_PHONE_GUTTER : 12,
    ...innerContentWrapperStyle,
  };
  if (isCollapsible) {
    tabsItems = tabsItems.map((tabItem) => ({
      ...tabItem,
      children: (
        <CollapsibleTabBody style={tabBodyStyle}>
          {tabsToComponents?.[tabItem.key]}
        </CollapsibleTabBody>
      ),
    }));
  } else if (isBottomNavigator) {
    tabsItems = tabsItems.map((tabItem) => {
      const isFlatListTab = flatListTabs?.includes(tabItem.key);
      return {
        ...tabItem,
        children: (
          <ScrollView
            loading={isLoading}
            onRefresh={
              getTabQueries(tabToUse) ? () => onRefresh(tabItem.key) : undefined
            }
            refreshing={isRefreshing(tabItem.key)}
            contentWrapperStyle={{
              ...tabBodyStyle,
              ...(isFlatListTab ? { padding: 0, height: "100%" } : {}),
            }}
          >
            {tabsToComponents?.[tabItem?.key]}
          </ScrollView>
        ),
      };
    });
  }

  const showTabsInHeader =
    !isBottomNavigator && !hideTabs && tabs.length > 0 && tabsToComponents;
  const hideTitle = !title || (showMobileSearch && searchOpen);

  const contextValue = useMemo(() => {
    return {
      setTabQueries,
    };
  }, []);

  const headerContent = (
    <>
      {showMobileSearch && useMobileSearch && (
        <SearchWrapper
          $searchOpen={searchOpen}
          $safeAreaInset={safeAreaInset}
          $contentSafeAreaInset={contentSafeAreaInset}
        >
          <MobileSearch searchOpen={searchOpen} setSearchOpen={setSearchOpen} />
        </SearchWrapper>
      )}
      {!hideTitle && <DrawerTitle title={title} isMobile={isMobile} />}
      {extraComponent}
      {showTabsInHeader && (
        <Tabs
          activeKey={tabToUse}
          onTabClick={localOnTabChange}
          size="small"
          tabBarStyle={{
            marginTop: 4,
            // New look: with the content's own 12px top margin, the
            // first card sits 20px under the tab bar (section gap).
            marginBottom: isMobile ? 0 : isNewLook ? 8 : 24,
            ...tabsStyle,
          }}
          items={tabsItems}
        />
      )}
    </>
  );

  return (
    <ExtraDetailsDrawerContext.Provider value={contextValue}>
      <StyledDrawer
        {...props}
        $bannerHeight={bannerHeight}
        $isMobile={isBottomNavigator}
      >
        {extension && (
          <Extension
            style={{ width: extensionWidth }}
            $hideBorder={hideExtensionBorder}
          >
            {extension}
          </Extension>
        )}
        <ContentWrapper
          ref={drawerContentRef}
          id="drawer-content"
          $hasExtension={!!extension}
          $width={mainContentWidth}
          $minWidth={minWidth}
        >
          {showDemoBanner && (
            <div ref={measureBannerRef}>
              <DemoModeBanner
                onSwitchToUserWorkspace={closeDrawerBeforeWorkspaceSwitch}
              />
            </div>
          )}
          {isCollapsible ? (
            <CollapsibleScroll
              ref={collapsibleScrollRef}
              data-testid={DRAWER_SCROLL_ID}
              onScroll={(event) =>
                setCollapsedBarVisible(
                  event.currentTarget.scrollTop > NEW_LOOK_DRAWER_CLOSE_HIT
                )
              }
            >
              <CollapsedBar>
                <CollapsedBarInner
                  $visible={collapsedBarVisible}
                  aria-hidden={!collapsedBarVisible}
                  data-testid={DRAWER_COLLAPSED_BAR_ID}
                >
                  {collapsedTitle}
                </CollapsedBarInner>
              </CollapsedBar>
              <HeaderWrapper $static={true}>{headerContent}</HeaderWrapper>
              {getContent()}
            </CollapsibleScroll>
          ) : (
            <>
              <HeaderWrapper>{headerContent}</HeaderWrapper>
              {getContent()}
            </>
          )}
        </ContentWrapper>
      </StyledDrawer>
    </ExtraDetailsDrawerContext.Provider>
  );
};

export default ExtraDetailsDrawer;

const StyledDrawer = styled(Drawer)<{
  $bannerHeight: number;
  $isMobile?: boolean;
}>`
  .ant-drawer-body {
    padding-bottom: 0 !important;
  }
  .ant-drawer-close {
    transform: translateY(${(p) => p.$bannerHeight}px);
  }
`;
const ContentWrapper = styled.div<{
  $hasExtension?: boolean;
  $width?: number;
  $minWidth?: number;
}>`
  ${(p) =>
    (!p.$minWidth || p.$minWidth < DEFAULT_DRAWER_WIDTH) &&
    `
    max-width: ${DEFAULT_DRAWER_WIDTH}px;
  `};
  margin: 0 auto;
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  ${(p) =>
    p.$hasExtension &&
    `
  width: ${p.$width}px;
  `}
`;
const InnerContentWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  padding: 0;
  margin-top: 0;

  @media (min-width: ${phoneMinWidth}) {
    padding: 0 12px;
    margin-top: 12px;
  }
  @media (min-width: ${MOBILE_SEARCH}) {
    padding: 0 24px;
  }
`;
const HeaderWrapper = styled.div<{ $static?: boolean }>`
  position: ${(p) => (p.$static ? "relative" : "sticky")};
  top: 0;
  background-color: ${(p) => p.theme.colors.layoutBgColor};
  z-index: ${(p) => (p.$static ? "auto" : 30)};
  padding: 12px 24px 0 24px;
  @media (max-width: ${MOBILE_SEARCH}) {
    padding: 12px 12px 0 12px;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      /* Phone: the 16px gutter, and a 40px title row the close button
         centres on. */
      @media (max-width: ${MOBILE_SEARCH}) {
        padding: ${NEW_LOOK_DRAWER_HEADER_TOP}px ${NEW_LOOK_PHONE_GUTTER}px 0;
      }
    `}
`;
const Extension = styled.div<{ $hideBorder?: boolean }>`
  ${(p) =>
    !p.$hideBorder &&
    `
    border-right: ${
      p.theme.isNewLook
        ? p.theme.borders.lightest
        : `2px solid ${p.theme.colors.mediumBorderGrey}`
    };
  `}
`;
const SearchWrapper = styled.div<{
  $searchOpen?: boolean;
  $safeAreaInset?: Partial<Inset>;
  $contentSafeAreaInset?: Partial<Inset>;
}>`
  color: ${(p) => p.theme.colors.secondaryText};
  max-width: 200px;
  display: flex;
  position: ${(p) => (p.$searchOpen ? "relative" : "absolute")};
  z-index: 40;
  top: ${(p) => {
    const addPadding = IS_TELEGRAM_WEB_APP && isMobile;
    const topPadding = addPadding
      ? (p.$safeAreaInset?.top ?? 0) + (p.$contentSafeAreaInset?.top ?? 0)
      : 0;
    return p.$searchOpen ? 0 : topPadding + 14;
  }}px;
`;
const ContentContainer = styled.div`
  width: 100%;
`;
const CollapsibleScroll = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior-y: contain;
`;
const CollapsedBar = styled.div`
  position: sticky;
  top: 0;
  height: 0;
  z-index: 30;
`;
const CollapsedBarInner = styled.div<{ $visible: boolean }>`
  display: flex;
  align-items: center;
  height: ${COLLAPSED_BAR_HEIGHT}px;
  padding: ${NEW_LOOK_DRAWER_HEADER_TOP}px
    ${NEW_LOOK_PHONE_GUTTER + NEW_LOOK_DRAWER_CLOSE_HIT - 8}px 0
    ${NEW_LOOK_PHONE_GUTTER}px;
  background: ${(p) => p.theme.colors.layoutBgColor};
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  pointer-events: ${(p) => (p.$visible ? "auto" : "none")};
  transition: opacity 0.15s ease;
`;
const CollapsibleTabBody = styled.div`
  padding-top: 24px;
  padding-bottom: 24px;
`;

const DrawerTitle = ({
  title,
  isMobile,
}: {
  title: React.JSX.Element | string;
  isMobile: boolean;
}) => {
  return (
    <TitleWrapper>
      <Text
        as="div"
        style={{ width: "100%" }}
        $size={isMobile ? typography.fontSizes.M : typography.fontSizes.L}
      >
        {title}
      </Text>
    </TitleWrapper>
  );
};

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 30px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      @media (max-width: ${MOBILE_SEARCH}) {
        min-height: ${NEW_LOOK_DRAWER_CLOSE_HIT}px;
      }
    `}
`;
